import type {
    CreateGeneralUserRequestBody,
    CreateGeneralUserResponse,
    GetGeneralUserListRequestQuery,
    GetGeneralUserListResponse,
    GetGeneralUserResponse,
    UpdateGeneralUserRequestBody,
    UpdateGeneralUserResponse,
} from '@engagement-web-app/common';
import { APIError } from './types';

export async function fetchSystemGeneralUsers(
    organizationId: string,
    { from, limit }: GetGeneralUserListRequestQuery
): Promise<GetGeneralUserListResponse> {
    try {
        const params = new URLSearchParams();
        if (from !== undefined) params.set('from', String(from));
        if (limit !== undefined) params.set('limit', String(limit));
        const res = await fetch(`/api/system/organizations/${organizationId}/users?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchGeneralUser(organizationId: string, userId: string): Promise<GetGeneralUserResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/users/${userId}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function createGeneralUser(
    organizationId: string,
    body: CreateGeneralUserRequestBody
): Promise<CreateGeneralUserResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function updateGeneralUser(
    organizationId: string,
    userId: string,
    body: UpdateGeneralUserRequestBody
): Promise<UpdateGeneralUserResponse> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/users/${userId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function deleteGeneralUser(organizationId: string, userId: string): Promise<void> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/users/${userId}`, {
            method: 'DELETE',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function resetGeneralUserPassword(organizationId: string, userId: string): Promise<void> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/users/${userId}/password-reset`, {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function resetGeneralUserMfa(organizationId: string, userId: string): Promise<void> {
    try {
        const res = await fetch(`/api/system/organizations/${organizationId}/users/${userId}/mfa-reset`, {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}
